import React, { useState, useMemo, useEffect } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment-timezone';
import { getUserProfile } from '@store/common/selectors';
import { useWindowSize } from 'react-use';
import { Table, Button, Modal, type TableProps, message } from 'antd';
import { produce } from 'immer';
import { ExclamationCircleFilled, CloseCircleFilled, CheckCircleFilled } from '@ant-design/icons';
import { useAtomValue, useSetAtom, useAtom } from 'jotai';
import apptracker from '@utils/sensor/apptracker';
import * as Events from '@src/utils/sensor/track';
import { Loading } from '../../common/Loading';
import { Retry } from '../../common/Retry';
import { DataSource, gerResponseErrorMessage, isAllCategory } from '../../utils';
import { useReadOnlyRole, useAllowCreateFilter } from '../hooks';
import * as atoms from '../atom';
import {
  getColumns,
  checkIsDisabledRow,
  checkIsFilterMatched,
  checkCanEdit,
  checkCanDelete,
  capitalizeFirstLetter,
} from './utils';
import FilterSearchBox, { type AdvancedSearchValue } from './FilterSearchBox';
import * as Styled from './styled';
import { type SavedFilter } from '../../../types';

interface Props {
  savedFilters: SavedFilter[];
  isSavedFiltersLoading: boolean;
  isSavedFiltersError: boolean;
}

const SavedFilterList = (props: Props) => {
  const { isSavedFiltersLoading, isSavedFiltersError, savedFilters = [] } = props;
  const user = useSelector(getUserProfile);
  const dataSource = useAtomValue(atoms.dataSource);
  const disableCreateFilter = !useAllowCreateFilter(dataSource);
  const [currentCategory, setCurrentCategory] = useAtom(atoms.currentCategory);
  const setFilterCreate = useSetAtom(atoms.filterCreate);
  const callbacks = useAtomValue(atoms.callbacks);
  const readOnly = useReadOnlyRole();
  const windowSize = useWindowSize();
  const [pageNumber, setPageNumber] = useState(1);
  const [searchText, setSearchText] = useState('');
  const [advancedSearch, setAdvancedSearch] = useState<AdvancedSearchValue>(() => {
    const defaultStates = { hiddenFilters: user.hidden };

    return currentCategory.category && currentCategory.subCategory
      ? { ...defaultStates, ...currentCategory }
      : defaultStates;
  });
  const [selectedToDelete, setSelectedToDelete] = useState<SavedFilter[]>([]);
  const selectedToDeleteIds = React.useMemo(
    () =>
      selectedToDelete
        .map((it) => it.fid)
        .filter((it) => {
          const filter = savedFilters.find((item) => item.fid === it);

          if (!filter) return false;

          return (
            checkCanEdit({ user, dataSource, savedFilter: filter }) &&
            checkCanDelete({ user, dataSource, savedFilter: filter })
          );
        }),
    [savedFilters, selectedToDelete, user, dataSource],
  );

  const filters = useMemo(() => {
    const categoryStr = `${currentCategory.category}/${currentCategory.subCategory}`;
    const { createdBy = '', createdOnStart, createdOnEnd, hiddenFilters } = advancedSearch;

    return savedFilters.filter((it) =>
      [
        isAllCategory(currentCategory.category) || it.category === categoryStr,
        checkIsFilterMatched(it, searchText),
        it.creator.toLowerCase().includes(createdBy.toLowerCase()),
        !createdOnStart ||
          moment(it.created * 1000).isSameOrAfter(moment(createdOnStart).startOf('day')),
        !createdOnEnd ||
          moment(it.created * 1000).isSameOrBefore(moment(createdOnEnd).endOf('day')),
        // filter to show hidden filters only when user is an internal user
        !user.hidden || hiddenFilters || !it.hidden,
      ].every(Boolean),
    );
  }, [savedFilters, currentCategory, searchText, advancedSearch, user]);

  const columns = useMemo(() => getColumns({ dataSource, user }), [dataSource, user]);

  const rowSelection = useMemo<TableProps<SavedFilter>['rowSelection']>(
    () => ({
      type: 'checkbox',
      preserveSelectedRowKeys: true,
      selectedRowKeys: selectedToDeleteIds,
      selections: [
        {
          key: 'clear',
          text: 'Clear all selections',
          onSelect: () => setSelectedToDelete([]),
        },
      ],
      getCheckboxProps: (record) => ({
        disabled:
          !checkCanEdit({ user, dataSource, savedFilter: record }) ||
          !checkCanDelete({ user, dataSource, savedFilter: record }),
      }),
      onChange: (selectedKeys, selectedRecords) => {
        setSelectedToDelete(selectedRecords);
      },
    }),
    [selectedToDeleteIds, user, dataSource],
  );

  // reset filter when data source changed
  useEffect(() => {
    setSearchText('');
    setAdvancedSearch(user.hidden ? { hiddenFilters: true } : {});
    setSelectedToDelete([]);
  }, [dataSource, user.hidden]);

  useEffect(() => {
    setAdvancedSearch((current) =>
      produce(current, (draft) => {
        if (currentCategory.category && currentCategory.subCategory) {
          draft.category = currentCategory.category;
          draft.subCategory = currentCategory.subCategory;
        } else {
          delete draft.category;
          delete draft.subCategory;
        }
      }),
    );
  }, [currentCategory]);

  useEffect(() => {
    setPageNumber(1);
  }, [filters]);

  const handleOnCreateFilter = () => {
    if (disableCreateFilter) return;

    setFilterCreate(true);
  };

  const handleDelete = () => {
    if (selectedToDeleteIds.length > 200) {
      message.error('Cannot delete more than 200 filters at once');
      return;
    }

    const modalCallbacks = Modal.confirm({
      title: `Delete Filters`,
      icon: <ExclamationCircleFilled />,
      content: (
        <>
          Do you want to delete
          {selectedToDeleteIds.length > 1 ? ` these filters` : ' this filter'}
          ?
          <br />
          {dataSource !== DataSource.app && (
            <div style={{ marginTop: 4 }}>
              Deleting {selectedToDeleteIds.length > 1 ? 'these filters' : 'this filter'} may cause
              components that are using the filters to malfunction.
            </div>
          )}
        </>
      ),
      okText: 'Delete',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          const res = await callbacks.onDeleteFilter?.(selectedToDeleteIds, dataSource);

          if (res?.success && res.failed) {
            const selectedFiltersMap = new Map(selectedToDelete.map((it) => [it.fid, it]));
            const resData = res.failed
              .map((id) => ({
                id,
                name: selectedFiltersMap.get(id)?.name,
                success: false,
                result: capitalizeFirstLetter(res.errors[id]),
              }))
              .concat(
                res.success.map((id) => ({
                  id,
                  name: selectedFiltersMap.get(id)?.name,
                  success: true,
                  result: 'Deleted successfully',
                })),
              );

            requestAnimationFrame(() => {
              modalCallbacks.update({
                type: 'error',
                title: res.success.length
                  ? `Partially successful, some filters cannot be deleted`
                  : `Failed to delete`,
                icon: <CloseCircleFilled />,
                width: 600,
                content: (
                  <Table
                    rowKey="id"
                    size="small"
                    scroll={{ y: 240 }}
                    pagination={false}
                    columns={[
                      {
                        title: '',
                        width: 40,
                        dataIndex: 'success',
                        align: 'center',
                        render: (value: boolean) =>
                          value ? (
                            <CheckCircleFilled style={{ fontSize: 20, color: '#52c41a' }} />
                          ) : (
                            <CloseCircleFilled style={{ fontSize: 20, color: '#ff4d4f' }} />
                          ),
                      },
                      {
                        title: 'ID',
                        dataIndex: 'id',
                        width: 80,
                      },
                      {
                        title: 'Name',
                        dataIndex: 'name',
                      },
                      {
                        title: 'Result',
                        dataIndex: 'result',
                      },
                    ]}
                    dataSource={resData}
                  />
                ),
                okText: 'OK',
                okButtonProps: { danger: false },
                onOk: () => {},
              });
            });

            return Promise.reject();
          }

          message.success(`Deleted successfully`);
        } catch (e) {
          const errorMessage = gerResponseErrorMessage(e as Error) || `Failed to delete filters`;
          message.error(errorMessage);
        } finally {
          apptracker(Events.FILTER_MANAGEMENT_BULK_DELETE, {
            count: selectedToDeleteIds.length,
            ids: selectedToDeleteIds,
          });
          setSelectedToDelete([]);
        }
      },
    });
  };

  const handleAdvancedSearch = (value: AdvancedSearchValue) => {
    setAdvancedSearch(value);

    if (value.category && value.subCategory) {
      setCurrentCategory({
        category: value.category,
        subCategory: value.subCategory,
      });
    } else {
      setCurrentCategory({
        category: 'ALL',
        subCategory: '',
      });
    }
  };

  const renderContent = () => {
    if (isSavedFiltersLoading) return <Loading />;

    if (isSavedFiltersError) return <Retry onClick={() => callbacks.onSavedFiltersRetry?.()} />;

    return (
      <Table
        rowKey="fid"
        sticky
        scroll={{ x: 900, y: 'calc(100% - 70px)', scrollToFirstRowOnChange: true }}
        rowSelection={rowSelection}
        dataSource={filters}
        columns={columns}
        pagination={{
          current: pageNumber,
          onChange: setPageNumber,
          pageSize: windowSize.height > 1000 ? 50 : 25,
          showSizeChanger: false,
        }}
        onRow={(record) => {
          if (checkIsDisabledRow(record, dataSource)) {
            return {
              className: 'disabled-row',
            };
          }
          return {};
        }}
      />
    );
  };

  return (
    <>
      <Styled.TopSection>
        <Styled.SubCategory>
          {isAllCategory(currentCategory.category) ? 'ALL' : currentCategory.subCategory}
        </Styled.SubCategory>
        <Styled.TopFilterBar>
          <Button
            type="primary"
            danger
            ghost
            disabled={!selectedToDeleteIds.length}
            onClick={handleDelete}
          >
            Delete{!!selectedToDeleteIds.length && `(${selectedToDeleteIds.length})`}
          </Button>
          <FilterSearchBox
            defaultValue={searchText}
            advancedSearch={advancedSearch}
            hiddenEnabled={user.hidden}
            onSearch={setSearchText}
            onAdvancedSearch={handleAdvancedSearch}
          />
          {!readOnly && (
            <Button
              type="primary"
              ghost
              disabled={disableCreateFilter}
              onClick={handleOnCreateFilter}
            >
              + New Filter
            </Button>
          )}
        </Styled.TopFilterBar>
      </Styled.TopSection>
      <Styled.TableContainer>{renderContent()}</Styled.TableContainer>
    </>
  );
};

export default React.memo(SavedFilterList);
